/**
 * @file index.tsx
 *
 * @fileoverview Adds metatag information to the current page based on the url.
 */

import Head from 'next/head';
import React, { useContext } from 'react';
import LazyHydrate from 'react-lazy-hydration';
import {
  CANONICAL_QUERY_PARAM_EXCLUDE_PATTERNS,
  excludeCanonicalPathQueryParamsByPattern,
} from '~/jobiqo/components/Search/utils/searchHelpers';
import { AppContext, PathContext } from '~/jobiqo/context/app';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';
import { Metatag } from '~/jobiqo/types/types';
import { isCanonicalProvidedByChild } from '~/jobiqo/utils/routes';
import { withLocalePrefix } from '~/jobiqo/utils/links';

interface MetatagsProps {
  /**
   * The current path.
   */
  path?: string;
  /**
   * The current host.
   */
  host?: string;
  metaTags: Metatag[];
  /**
   * The locale of the canonical URL. Defaults to the current locale.
   */
  canonicalLocale?: string;
}

const getCanonicalPath = (meta: Metatag[], host: string, cleanPath: string) => {
  // Either use canonical tag from GraphQL API or create in frontend.
  const canonicalPath =
    meta.find((metaTag: Metatag) => metaTag.meta === 'canonical')?.value ??
    host + cleanPath;

  return excludeCanonicalPathQueryParamsByPattern(
    canonicalPath,
    CANONICAL_QUERY_PARAM_EXCLUDE_PATTERNS,
  );
};

export const MetatagsInclude = ({
  metaTags,
  host = '',
  path = '',
  canonicalLocale,
}: MetatagsProps) => {
  const { pathname } = useContext(PathContext);
  const { locale } = useContext(AppContext);
  const { APP } = useFrontendConfig();

  return (
    <LazyHydrate whenIdle={true}>
      <Head>
        {!isCanonicalProvidedByChild(path, pathname) && (
          <link
            rel="canonical"
            href={withLocalePrefix(
              getCanonicalPath(metaTags, host, path),
              host,
              APP.LOCALES,
              canonicalLocale ?? locale,
            )}
            key="canonical"
          />
        )}
        {metaTags.map((metaInfo: Metatag) => {
          switch (metaInfo.meta) {
            case 'title':
              return <title key={metaInfo.meta}>{metaInfo.value}</title>;
            case 'canonical':
              return null;
            case 'og:image':
              return (
                metaInfo.meta &&
                metaInfo.value && (
                  <meta
                    key={metaInfo.meta}
                    property={metaInfo.meta}
                    content={metaInfo.value}
                  />
                )
              );
            default:
              return (
                metaInfo.meta &&
                metaInfo.value && (
                  <meta
                    key={metaInfo.meta}
                    name={metaInfo.meta}
                    content={metaInfo.value}
                    property={metaInfo.meta}
                  />
                )
              );
          }
        })}
      </Head>
    </LazyHydrate>
  );
};
